
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const CloudMigrationGuide: React.FC = () => {
  return (
    <>
      <p className="mb-6 text-gray-700 leading-relaxed">
        Cloud migration can transform your business operations, reduce IT costs, and improve scalability. However, many small businesses struggle with knowing where to start. This comprehensive guide provides a proven framework that takes the guesswork out of moving your operations to the cloud safely and efficiently.
      </p>
      
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue" id="assessment">Phase 1: Assessment and Planning</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        Before moving anything to the cloud, you need a clear understanding of your current IT environment and business requirements. This assessment phase is critical for avoiding costly mistakes and ensuring your migration supports your business goals.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">Current State Analysis</h3>
      <ul className="list-disc ml-6 mb-6 text-gray-700 leading-relaxed">
        <li className="mb-2">Inventory all applications, databases, and systems currently in use</li>
        <li className="mb-2">Document data flows and dependencies between systems</li>
        <li className="mb-2">Assess current security measures and compliance requirements</li>
        <li className="mb-2">Calculate existing IT costs including hardware, software, and maintenance</li>
        <li className="mb-2">Identify performance bottlenecks and pain points</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed">
        Many businesses benefit from working with <Link to="/services/managed-it" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">managed IT services</Link> providers during this phase to ensure nothing is overlooked in the assessment process.
      </p>
      
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue" id="strategy">Phase 2: Migration Strategy Selection</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        Not all applications are suitable for immediate cloud migration. Understanding the different migration strategies helps you make the right choices for each component of your IT infrastructure.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">The 6 R's of Cloud Migration</h3>
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <ul className="list-disc ml-6 text-gray-700 leading-relaxed space-y-2">
          <li><strong>Rehost (Lift and Shift):</strong> Move applications to cloud without changes - fastest but least optimized</li>
          <li><strong>Replatform:</strong> Make minimal changes to optimize for cloud environments</li>
          <li><strong>Refactor:</strong> Redesign applications to fully leverage cloud-native features</li>
          <li><strong>Repurchase:</strong> Replace existing software with cloud-based SaaS solutions</li>
          <li><strong>Retain:</strong> Keep certain systems on-premises for compliance or technical reasons</li>
          <li><strong>Retire:</strong> Decommission applications that are no longer needed</li>
        </ul>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue" id="execution">Phase 3: Migration Execution</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        The execution phase requires careful coordination and testing to minimize business disruption. A phased approach reduces risk and allows for learning from early migrations.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">Migration Best Practices</h3>
      <ul className="list-disc ml-6 mb-6 text-gray-700 leading-relaxed">
        <li className="mb-2">Start with non-critical applications to build confidence and experience</li>
        <li className="mb-2">Implement robust backup and rollback procedures before each migration</li>
        <li className="mb-2">Test thoroughly in staging environments before production cutover</li>
        <li className="mb-2">Plan for extended parallel running of old and new systems</li>
        <li className="mb-2">Provide comprehensive user training on new cloud-based workflows</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed">
        Security considerations are paramount during migration. Our <Link to="/services/cybersecurity" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">cybersecurity services</Link> ensure your data remains protected throughout the migration process and in your new cloud environment.
      </p>
      
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue" id="optimization">Phase 4: Post-Migration Optimization</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        Migration completion is just the beginning. Ongoing optimization ensures you realize the full benefits of cloud computing while controlling costs.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">Optimization Areas</h3>
      <ul className="list-disc ml-6 mb-6 text-gray-700 leading-relaxed">
        <li className="mb-2">Cost optimization through right-sizing and reserved instances</li>
        <li className="mb-2">Performance tuning based on actual usage patterns</li>
        <li className="mb-2">Security hardening and compliance validation</li>
        <li className="mb-2">Automation of routine maintenance and scaling tasks</li>
        <li className="mb-2">Integration optimization for improved workflow efficiency</li>
      </ul>
      
      <div className="bg-itmgmt-light-blue/20 border-l-4 border-itmgmt-medium-blue p-6 rounded-lg my-8">
        <h3 className="text-xl font-semibold mb-3 text-itmgmt-dark-blue">Common Migration Challenges</h3>
        <p className="mb-4 text-gray-700 leading-relaxed">
          Learn from others' experiences and avoid these frequent pitfalls:
        </p>
        <ul className="list-disc ml-6 mb-2 text-gray-700 leading-relaxed">
          <li className="mb-2">Underestimating data transfer time and costs</li>
          <li className="mb-2">Insufficient testing leading to performance issues</li>
          <li className="mb-2">Lack of change management resulting in user resistance</li>
          <li className="mb-2">Inadequate security configuration in new environment</li>
          <li className="mb-2">Poor cost monitoring leading to budget overruns</li>
        </ul>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue">Choosing the Right Cloud Platform</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        The choice of cloud platform significantly impacts your migration success. Consider these factors when evaluating options:
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">Key Selection Criteria</h3>
      <ul className="list-disc ml-6 mb-6 text-gray-700 leading-relaxed">
        <li className="mb-2">Compatibility with your existing technology stack</li>
        <li className="mb-2">Geographic availability and data residency requirements</li>
        <li className="mb-2">Pricing model alignment with your usage patterns</li>
        <li className="mb-2">Security certifications and compliance capabilities</li>
        <li className="mb-2">Support quality and availability</li>
        <li className="mb-2">Integration capabilities with your existing tools</li>
      </ul>
      
      <p className="mb-6 text-gray-700 leading-relaxed">
        Our <Link to="/services/cloud-solutions" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">cloud solutions</Link> team can help evaluate platforms and design the optimal architecture for your specific needs.
      </p>
      
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue">Measuring Migration Success</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        Establish clear metrics to evaluate the success of your cloud migration and identify areas for continued improvement.
      </p>
      
      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">Key Performance Indicators</h3>
      <div className="bg-green-50 p-6 rounded-lg mb-6">
        <ul className="list-disc ml-6 text-gray-700 leading-relaxed space-y-2">
          <li><strong>Cost Metrics:</strong> Total cost of ownership reduction, operational cost savings</li>
          <li><strong>Performance Metrics:</strong> Application response times, system availability, scalability</li>
          <li><strong>Security Metrics:</strong> Incident reduction, compliance achievement, vulnerability management</li>
          <li><strong>Business Metrics:</strong> Employee productivity, customer satisfaction, time to market</li>
        </ul>
      </div>
      
      <div className="bg-itmgmt-light-blue p-6 rounded-lg mt-8 mb-6">
        <h3 className="text-xl font-semibold mb-3 text-itmgmt-dark-blue">Ready to start your cloud migration journey?</h3>
        <p className="mb-4 text-gray-700 leading-relaxed">
          Our experienced team can guide you through every phase of your cloud migration, from initial assessment to ongoing optimization. We'll help you avoid common pitfalls and ensure your migration delivers the expected benefits.
        </p>
        <div className="flex flex-wrap gap-4">
          <Button asChild className="mt-2">
            <Link to="/book">Schedule a Migration Assessment</Link>
          </Button>
          <Button asChild variant="outline" className="mt-2">
            <Link to="/contact">Discuss Your Cloud Strategy</Link>
          </Button>
        </div>
      </div>
      
      <div className="mt-12 pt-8 border-t border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-itmgmt-dark-blue">Quick Navigation</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <li>
            <Link to="#assessment" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue">
              Phase 1: Assessment and Planning
            </Link>
          </li>
          <li>
            <Link to="#strategy" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue">
              Phase 2: Migration Strategy
            </Link>
          </li>
          <li>
            <Link to="#execution" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue">
              Phase 3: Migration Execution
            </Link>
          </li>
          <li>
            <Link to="#optimization" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue">
              Phase 4: Post-Migration Optimization
            </Link>
          </li>
        </ul>
      </div>
    </>
  );
};

export default CloudMigrationGuide;
